import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import {
  absoluteUrl,
  breadcrumbSchema,
  itemListSchema,
  JsonLd,
} from '../../../components/JsonLd'
import { Section } from '../../../components/Section'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import { filterIntegrations } from '../filterIntegrations'
import { IntegrationGrid } from '../IntegrationGrid'
import { IntegrationsHeader } from '../IntegrationsHeader'
import { IntegrationsSidebar } from '../IntegrationsSidebar'
import type { Data } from './+data'

export default function SourcesPage() {
  const intl = useIntl()
  const routes = useRoutes()

  const { integrations } = useData<Data>()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredIntegrations = useMemo(
    () => filterIntegrations(integrations, searchQuery),
    [integrations, searchQuery]
  )

  const pageTitle = intl.formatMessage({
    id: 'integrations.sources.title',
    defaultMessage: 'Sources for Slite AI search',
  })

  const pageDescription = intl.formatMessage({
    id: 'integrations.sources.description',
    defaultMessage:
      'Connect Notion, Google Drive, Slack, GitHub and more so Slite can search across all your company knowledge.',
  })

  const integrationsLabel = intl.formatMessage({
    id: 'integrations.breadcrumb.integrations',
    defaultMessage: 'Integrations',
  })

  const itemList = itemListSchema({
    items: integrations.map((integration) => ({
      name: integration.name,
      url: absoluteUrl(
        routes.integrations.detail({ slug: integration.slug.current })
      ),
    })),
  })

  const breadcrumbs = breadcrumbSchema({
    items: [
      { name: 'Slite', url: absoluteUrl('/') },
      {
        name: integrationsLabel,
        url: absoluteUrl(routes.integrations.index()),
      },
      {
        name: pageTitle,
        url: absoluteUrl(routes.integrations.sources()),
      },
    ],
  })

  return (
    <SlitePage
      canonicalUrl={routes.integrations.sources()}
      background="moon-dust-lighter"
      title={pageTitle}
      description={pageDescription}
    >
      <JsonLd data={itemList} />
      <JsonLd data={breadcrumbs} />
      <IntegrationsHeader
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />
      <Section background="moon-dust-lighter" containerTopPadding={0}>
        <div className="grid-12 grid-gap-40">
          <div className="grid-span-3 grid-span-12-small">
            <IntegrationsSidebar current="source" />
          </div>
          <div className="grid-span-9 grid-span-12-small">
            <IntegrationGrid integrations={filteredIntegrations} columns={3} />
          </div>
        </div>
      </Section>
      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
